import { AlertTriangle, BellRing, Check, ExternalLink } from 'lucide-react';
import type { PostDTO } from '@bb/shared';
import { relative, utcStamp } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { useEmergencyAlerts, useEmergencyNotifications } from './useEmergencyAlerts';

/**
 * Emergencies pinned above the feed until acknowledged.
 *
 * Laid out as a horizontal strip rather than a stack: the row keeps a fixed
 * height whatever the number of alerts, so the feed underneath never jumps as
 * they arrive and are cleared.
 */
export function EmergencyBanner({ onAcknowledge }: { onAcknowledge: (post: PostDTO) => void }) {
  const { alerts } = useEmergencyAlerts();
  // Only for the enable button; the notifications themselves fire from the
  // shell so they reach the operator on any screen.
  const { permission, requestPermission } = useEmergencyNotifications();

  if (alerts.length === 0) return null;

  return (
    <section className="shrink-0 border-b border-alert/40 bg-alert-dim/40">
      <header className="flex items-center gap-2 px-3 pt-2">
        <AlertTriangle size={12} className="shrink-0 text-alert" strokeWidth={2} />
        <span className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-alert">
          {alerts.length} unacknowledged
        </span>

        {permission === 'default' ? (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto text-alert hover:text-alert"
            onClick={() => void requestPermission()}
            icon={<BellRing size={11} />}
          >
            Desktop alerts
          </Button>
        ) : null}
      </header>

      <div className="flex gap-2 overflow-x-auto px-3 py-2">
        {alerts.map((post) => (
          <article
            key={post.id}
            className="flex w-[300px] shrink-0 flex-col justify-between gap-2 rounded-sharp border border-alert/40 bg-panel/80 p-2.5"
          >
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="data-mono truncate text-[11px] text-alert">
                  @{post.author.handle}
                </span>
                {post.analysis ? (
                  <span className="data-mono text-[10px] text-alert/70">
                    {post.analysis.importance}
                  </span>
                ) : null}
                <span
                  className="data-mono ml-auto shrink-0 text-[10px] text-ink-faint"
                  title={utcStamp(post.postedAt)}
                >
                  {relative(post.postedAt)}
                </span>
              </div>

              <p className="mt-1 line-clamp-3 text-[13px] leading-snug text-ink">
                {post.analysis?.summary ?? post.text}
              </p>
            </div>

            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-alert/40 text-alert hover:border-alert hover:text-alert"
                onClick={() => onAcknowledge(post)}
                icon={<Check size={11} />}
              >
                Ack
              </Button>
              <a
                href={post.url}
                target="_blank"
                rel="noreferrer noopener"
                title="Open on X"
                aria-label="Open on X"
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-sharp border border-line-bright text-ink-faint transition-colors hover:border-accent hover:text-accent"
              >
                <ExternalLink size={13} strokeWidth={1.75} />
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
